import type { ReactNode } from 'react';
import { WarningIcon } from '@phosphor-icons/react';
import { Num, T, Var, useGT } from 'gt-react';
import type {
  WorkbookDefinition,
  WorkbookRun,
  WorkbookRunComparison,
  WorkbookRunComparisonAlert,
  WorkbookSampleRate,
  WorkbookSampleSourceMethod,
  WorkbookSampleStratumKind,
  WorkbookSamplingReport,
} from '@erp/shared-types';

/** A rate gap past this is worth pointing out. */
const RATE_GAP = 0.1;

function itemNamer(definition: Pick<WorkbookDefinition, 'sources' | 'sheets'>) {
  return (id: string) =>
    [...definition.sources, ...definition.sheets].find((item) => item.id === id)
      ?.name ?? '';
}

function percent(value: number | null): string {
  return value === null ? '—' : `${Math.round(value * 1000) / 10}%`;
}

function formatFigure(value: number | null): string {
  return value === null
    ? '—'
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** A report table's header row: each label, and whether it heads a figure. */
function ReportTableHead({
  cells,
}: {
  cells: readonly { label: ReactNode; figure?: boolean }[];
}) {
  return (
    <thead className="text-muted-foreground">
      <tr>
        {cells.map(({ label, figure }, index) => (
          <th
            // A header's cells never reorder.
            // react-doctor-disable-next-line react-doctor/no-array-index-as-key
            key={index}
            className={`px-1 font-normal ${figure ? 'text-right' : 'text-left'}`}
          >
            {label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** One drift alert in words: what moved, in which Worksheet, from what to what. */
function useAlertText(
  definition: Pick<WorkbookDefinition, 'sources' | 'sheets'>,
) {
  const gt = useGT();
  const name = itemNamer(definition);
  return (alert: WorkbookRunComparisonAlert) => {
    const sheet = name(alert.sheetId);
    const column = alert.column ?? '';
    switch (alert.kind) {
      case 'rows':
        return gt('{sheet} has {current} rows, not about {baseline}', {
          context: 'workbook run drift alert',
          sheet,
          current: formatFigure(alert.current),
          baseline: formatFigure(alert.baseline),
        });
      case 'miss-rate':
        return gt(
          'The lookup in {column} of {sheet} found no match for {current} of rows, not {baseline}',
          {
            context: 'workbook run drift alert',
            column,
            sheet,
            current: percent(alert.current),
            baseline: percent(alert.baseline),
          },
        );
      case 'categories':
        return gt(
          '{share} of rows in {sheet} moved to other values of {column}',
          {
            context: 'workbook run drift alert',
            share: percent(alert.current),
            sheet,
            column,
          },
        );
      case 'total':
        return gt(
          'The total of {column} in {sheet} is {current}, not about {baseline}',
          {
            context: 'workbook run drift alert',
            column,
            sheet,
            current: formatFigure(alert.current),
            baseline: formatFigure(alert.baseline),
          },
        );
    }
  };
}

/** A Run's drift alerts against each earlier Run it was compared with. Warnings only. */
function RunDrift({
  comparisons,
  definition,
}: {
  comparisons: readonly WorkbookRunComparison[];
  definition: Pick<WorkbookDefinition, 'sources' | 'sheets'>;
}) {
  const alertText = useAlertText(definition);
  const alerted = comparisons.filter((comparison) => comparison.alerts.length);
  if (!alerted.length) return null;
  return (
    <div className="mt-1 flex flex-col gap-1">
      {alerted.map((comparison) => (
        <div
          key={`${comparison.kind}:${comparison.baselineRunId}`}
          role="status"
          className="rounded bg-amber-50 p-1.5 text-amber-900 dark:bg-amber-950 dark:text-amber-200"
        >
          <p className="flex items-center gap-1 font-medium">
            <WarningIcon className="size-3.5 shrink-0" />
            {comparison.kind === 'sample' ? (
              <T context="workbook run drift heading">
                Different from the sample before it
              </T>
            ) : (
              <T context="workbook run drift heading">
                Different from the previous run
              </T>
            )}
          </p>
          <ul className="ml-5 list-disc">
            {comparison.alerts.map((alert) => (
              <li key={`${alert.kind}:${alert.sheetId}:${alert.column ?? ''}`}>
                {alertText(alert)}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function RateCells({ rate }: { rate: WorkbookSampleRate }) {
  const gap =
    rate.full !== null &&
    rate.sample !== null &&
    Math.abs(rate.full - rate.sample) > RATE_GAP;
  return (
    <>
      <td className="px-1 text-right tabular-nums">{percent(rate.full)}</td>
      <td
        className={`px-1 text-right tabular-nums ${gap ? 'font-medium text-amber-700 dark:text-amber-400' : ''}`}
      >
        {percent(rate.sample)}
      </td>
    </>
  );
}

/** How a Run on sample's subset compares with the data it came from. */
function SampleReport({
  report,
  definition,
}: {
  report: WorkbookSamplingReport;
  definition: Pick<WorkbookDefinition, 'sources' | 'sheets'>;
}) {
  const gt = useGT();
  const name = itemNamer(definition);
  const methods: Record<WorkbookSampleSourceMethod, string> = {
    key: gt('By the key', { context: 'workbook sample source method' }),
    derived: gt('Narrowed by lookups', {
      context: 'workbook sample source method',
    }),
    whole: gt('All rows', { context: 'workbook sample source method' }),
  };
  const strata: Record<WorkbookSampleStratumKind, string> = {
    declared: gt('Value', { context: 'workbook sample stratum kind' }),
    blank: gt('Blank', { context: 'workbook sample stratum kind' }),
    'text-in-number': gt('Error or text in a number', {
      context: 'workbook sample stratum kind',
    }),
    top: gt('Largest 1%', { context: 'workbook sample stratum kind' }),
    'lookup-miss': gt('No lookup match', {
      context: 'workbook sample stratum kind',
    }),
    'blank-key': gt('Blank key', { context: 'workbook sample stratum kind' }),
  };
  const drifting = report.distance > RATE_GAP;
  const emptyLabel = gt('(Empty)', { context: 'workbook empty value' });
  return (
    <details className="mt-1">
      <summary
        className={`cursor-pointer ${drifting ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}
      >
        <T context="workbook sample report summary">
          Sample of <Num>{report.keys.sample}</Num> of{' '}
          <Num>{report.keys.full}</Num> key values · differs from the full data
          by up to <Var>{percent(report.distance)}</Var>
        </T>
      </summary>
      <div className="mt-1 flex flex-col gap-2 overflow-x-auto">
        <table className="w-full">
          <ReportTableHead
            cells={[
              { label: <T context="workbook sample report column">Data</T> },
              { label: <T context="workbook sample report column">Read</T> },
              {
                label: (
                  <T context="workbook sample report column">Rows in sample</T>
                ),
                figure: true,
              },
              {
                label: (
                  <T context="workbook sample report column">Rows in all</T>
                ),
                figure: true,
              },
            ]}
          />
          <tbody>
            {report.sources.map((source) => (
              <tr key={source.sourceId}>
                <td className="px-1">{name(source.sourceId)}</td>
                <td className="px-1">{methods[source.method]}</td>
                <td className="px-1 text-right tabular-nums">
                  {source.rows.sample.toLocaleString()}
                </td>
                <td className="px-1 text-right tabular-nums">
                  {source.rows.full.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {report.strata.length > 0 && (
          <table className="w-full">
            <ReportTableHead
              cells={[
                { label: <T context="workbook sample report column">Group</T> },
                {
                  label: (
                    <T context="workbook sample report column">
                      Keys in sample
                    </T>
                  ),
                  figure: true,
                },
                {
                  label: (
                    <T context="workbook sample report column">Keys in all</T>
                  ),
                  figure: true,
                },
              ]}
            />
            <tbody>
              {report.strata.map((stratum) => (
                <tr
                  key={`${stratum.kind}:${stratum.sourceId}:${stratum.column}:${stratum.value ?? ''}`}
                >
                  <td className="px-1">
                    {strata[stratum.kind]} · {name(stratum.sourceId)} ·{' '}
                    {stratum.column}
                    {stratum.value !== null &&
                      ` = ${stratum.value || emptyLabel}`}
                  </td>
                  <td className="px-1 text-right tabular-nums">
                    {stratum.keys.sample.toLocaleString()}
                  </td>
                  <td className="px-1 text-right tabular-nums">
                    {stratum.keys.full.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {report.lookups.length + report.columns.length > 0 && (
          <table className="w-full">
            <ReportTableHead
              cells={[
                {
                  label: <T context="workbook sample report column">Measure</T>,
                },
                {
                  label: (
                    <T context="workbook sample report column">All data</T>
                  ),
                  figure: true,
                },
                {
                  label: <T context="workbook sample report column">Sample</T>,
                  figure: true,
                },
              ]}
            />
            <tbody>
              {report.lookups.map((lookup) => (
                <tr
                  key={`${lookup.sheetId}:${lookup.from.sourceId}:${lookup.from.column}:${lookup.to.sourceId}:${lookup.to.column}`}
                >
                  <td className="px-1">
                    {gt('{from} found in {to}', {
                      context: 'workbook sample report lookup match rate',
                      from: `${name(lookup.from.sourceId)} · ${lookup.from.column}`,
                      to: `${name(lookup.to.sourceId)} · ${lookup.to.column}`,
                    })}
                  </td>
                  <RateCells rate={lookup.matchRate} />
                </tr>
              ))}
              {report.columns.map((column) => (
                <tr key={`${column.sourceId}:${column.column}`}>
                  <td className="px-1">
                    {gt('{column} blank', {
                      context: 'workbook sample report blank rate',
                      column: `${name(column.sourceId)} · ${column.column}`,
                    })}
                  </td>
                  <RateCells rate={column.blankRate} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </details>
  );
}

/** Below a Run in the Runs tab: its sample's report, when it is a Run on sample, and its drift alerts. */
export function WorkbookRunInsights({
  run,
  definition,
}: {
  run: Pick<WorkbookRun, 'sampling' | 'comparisons'>;
  definition: Pick<WorkbookDefinition, 'sources' | 'sheets'>;
}) {
  const report = run.sampling?.report;
  return (
    <>
      {report && <SampleReport report={report} definition={definition} />}
      <RunDrift comparisons={run.comparisons} definition={definition} />
    </>
  );
}
